import React, { useState, useEffect, useRef } from 'react';
import { MDBIcon } from 'mdbreact';
import firebase from '../../firebase';
import './ChatbotWidget.css';

// if (window.location.hostname === "localhost") {
//   firebase.functions().useFunctionsEmulator("http://localhost:5001");
// }

const ChatbotWidget = ({ userId, organizationId, companyId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const starterPrompts = [
    "注文をキャンセルするには？",
    "最近の注文を見る",
    "今月の支出はいくらですか？",
    "カタログから商品を探す",
    "サプライヤーを探す"
  ];

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Load initial welcome message
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: 'welcome',
        sender: 'ai',
        text: 'こんにちは！何かお手伝いできることはありますか？',
        timestamp: new Date()
      }]);
    }
  }, [isOpen]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const handleSend = async (text) => {
    if (!text.trim()) return;

    const userMsg = {
      id: Date.now().toString(),
      sender: 'user',
      text: text,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMsg]);
    setInputText('');
    setIsLoading(true);

    try {
      const askChatbot = firebase.functions().httpsCallable('askChatbot');
      const result = await askChatbot({ message: text, organizationId, companyId });
      
      const aiMsg = {
        id: (Date.now() + 1).toString(),
        sender: 'ai',
        text: result.data.reply || 'Уучлаарай, хариулт олдсонгүй.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMsg]);
    } catch (error) {
      console.error("Chat error:", error);
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        sender: 'ai',
        text: `Алдаа гарлаа: ${error.message}`,
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`chatbot-container ${isOpen ? 'open' : 'closed'}`}>
      {isOpen && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <div className="chatbot-title">
              <MDBIcon icon="robot" className="mr-2" />
              AI アシスタント
            </div>
            <button className="close-btn" onClick={toggleChat}>
              <MDBIcon icon="times" />
            </button>
          </div>

          <div className="chatbot-messages">
            {messages.map((msg) => {
              // Parse basic markdown like **bold**, [link](url), or malformed link(url)
              const parts = (msg.text || "").split(/(\*\*.*?\*\*|\[.*?\]\(.*?\)|\w+\(\/order\/.*?\))/g);
              return (
                <div key={msg.id} className={`message-wrapper ${msg.sender}`}>
                  <div className="message-bubble">
                    {parts.map((part, index) => {
                      if (part.startsWith('**') && part.endsWith('**')) {
                        return <strong key={index}>{part.slice(2, -2)}</strong>;
                      }
                      if (part.startsWith('[') && part.includes('](') && part.endsWith(')')) {
                        const match = part.match(/\[(.*?)\]\((.*?)\)/);
                        if (match) {
                          return <a key={index} href={match[2]} target="_blank" rel="noopener noreferrer" style={{ color: '#0056b3', textDecoration: 'underline' }}>{match[1]}</a>;
                        }
                      }
                      // Catch malformed link like OrderID(/order/OrderID)
                      if (part.includes('(/order/') && part.endsWith(')')) {
                        const match = part.match(/(.*?)\((.*?)\)/);
                        if (match) {
                          return <a key={index} href={match[2]} target="_blank" rel="noopener noreferrer" style={{ color: '#0056b3', textDecoration: 'underline' }}>{match[1]}</a>;
                        }
                      }
                      return <span key={index}>{part}</span>;
                    })}
                  </div>
                </div>
              );
            })}
            
            {isLoading && (
              <div className="message-wrapper ai">
                <div className="message-bubble typing-indicator">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}
            
            {messages.length === 1 && (
              <div className="starter-prompts">
                {starterPrompts.map((prompt, idx) => (
                  <button 
                    key={idx} 
                    className="prompt-chip"
                    onClick={() => handleSend(prompt)}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chatbot-input-area">
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend(inputText)}
              placeholder="こちらに質問を入力してください..."
              disabled={isLoading}
            />
            <button 
              className="send-btn" 
              onClick={() => handleSend(inputText)}
              disabled={!inputText.trim() || isLoading}
            >
              <MDBIcon icon="paper-plane" />
            </button>
          </div>
        </div>
      )}

      {!isOpen && (
        <button className="chatbot-fab" onClick={toggleChat}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        </button>
      )}
    </div>
  );
};

export default ChatbotWidget;
